import React, { useState } from 'react'
import { IconButton, Button, Link } from '@common/material'
import { Box, Text } from '@fundamentals'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { localizedStrings } from '@core/strings'

/**
 * MapDisclaimer component displays a disclaimer text with optional actions and a close button
 * that collapses into a small button when closed.
 *
 * Positioned by the parent overlay stack in `Map`, below the floating controls.
 *
 * Props:
 * - `text`: The disclaimer text to display
 * - `actions`: Optional array of disclaimer actions with URL and label
 */
type MapDisclaimerProps = {
  text: string
  actions?: { url: string; label: string }[]
}

export const MapDisclaimer: React.FC<MapDisclaimerProps> = ({
  text,
  actions,
}) => {
  const [isVisible, setIsVisible] = useState(true)

  const renderDisclaimerActions = () => {
    if (!actions || actions.length === 0) return null

    return (
      <Box sx={{ mt: 1 }}>
        {actions.map((action, index) => {
          // Ensure the URL is absolute
          const absoluteUrl =
            action.url.startsWith('http://') ||
            action.url.startsWith('https://')
              ? action.url
              : `https://${action.url}`

          return (
            <Box key={index} sx={{ mb: 1 }}>
              <Link href={absoluteUrl} target='_blank'>
                <Text
                  variant='body2'
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  {action.label}
                </Text>
              </Link>
            </Box>
          )
        })}
      </Box>
    )
  }

  if (isVisible) {
    return (
      <Box
        sx={{
          maxWidth: 300,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: 2,
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <Box sx={{ position: 'relative', pr: 3 }}>
          <IconButton
            size='small'
            onClick={() => setIsVisible(false)}
            sx={{
              position: 'absolute',
              right: -12,
              top: -12,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
          {text && (
            <Text variant='body2' color='text.secondary'>
              {text}
            </Text>
          )}
          {renderDisclaimerActions()}
        </Box>
      </Box>
    )
  }

  return (
    <Button
      variant='contained'
      size='small'
      startIcon={<InfoOutlinedIcon />}
      onClick={() => setIsVisible(true)}
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        color: 'text.secondary',
        boxShadow: 1,
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 1)',
        },
      }}
    >
      {localizedStrings.disclaimer}
    </Button>
  )
}
